import React, { PureComponent } from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import isEmpty from 'lodash/isEmpty';
require('draft-js/dist/Draft.css');
require('draft-js-anchor-plugin/lib/plugin.css');
require('draft-js-inline-toolbar-plugin/lib/plugin.css');
require('./linkPluginStyles.css');
import {
  RichUtils,
  EditorState,
  convertToRaw,
  convertFromRaw,
  ContentBlock,
  Modifier,
} from 'draft-js';
// @ts-expect-error draft-js does not have typescript definitions for this module
import DraftPasteProcessor from 'draft-js/lib/DraftPasteProcessor';
// @ts-expect-error draft-js does not have typescript definitions for this module
import BlockMapBuilder from 'draft-js/lib/BlockMapBuilder';
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import Editor from 'draft-js-plugins-editor';
import createMarkdownShortcutsPlugin from 'draft-js-markdown-shortcuts-plugin';
import { Collapse } from '@mui/material';
import createLinkPlugin from 'draft-js-anchor-plugin';
import createInlineToolbarPlugin from 'draft-js-inline-toolbar-plugin';
import { ItalicButton, BoldButton, UnderlineButton } from 'draft-js-buttons';
import Controls from './Controls';
import clsx from 'clsx';
import ErrorBoundary from '../ErrorBoundary';
import { Box } from '@lifeomic/chroma-react/components/Box';
import _invert from 'lodash/invert';

const styles = (theme: Theme) =>
  createStyles({
    editorRoot: {
      padding: theme.spacing(2),
      flexDirection: 'column',
      color: theme.palette.text.primary,
      '& .public-DraftEditor-content, & .DraftEditor-root': {
        color: theme.palette.text.primary,
      },
      '& span[data-text="true"]': {
        color: theme.palette.text.primary,
      },
      '& .public-DraftEditorPlaceholder-root': {
        color: theme.palette.text.hint,
      },
    },
    enabledEditorRoot: {
      cursor: 'text',
    },
    footnote: {
      cursor: 'pointer',
      fontSize: theme.pxToRem(13),
      lineHeight: 0,
      verticalAlign: 'super',
      'a&': {
        textDecoration: 'none',
        '&:hover, &:focus': {
          textDecoration: 'underline',
        },
      },
    },
  });

export interface MarkdownEditorOwnProps {
  disabled?: boolean;
  isEditing?: boolean;
  editorState?: EditorState;
  editorBlockStyle?: (block: ContentBlock) => string;
  onChange: (editorState: EditorState) => void;
  value: EditorState;
  whiteTheme?: boolean;
  editorClassName?: string;
  buttonControlClassName?: string;
  placeholderText?: string;
  getComponentForFootnote?: (
    footnoteId: string,
    text: string,
    decoratedText: string,
  ) => React.ReactNode;
  handlePastedFile?: (files: File[]) => void;
}

export type MarkdownEditorProps = MarkdownEditorOwnProps &
  WithStyles<typeof styles>;

export interface MarkdownEditorState {
  plugins: any[];
  darkThemeLinkPlugin: any;
  lightThemeLinkPlugin: any;
  themedLinkPlugin: any;
  inlineToolbarPlugin: any;
}

type RemarkableState = {
  tokens: {
    type: string;
    content?: string;
    alt?: string;
    src?: string;
    id?: number;
    label?: string;
  }[];
  env?: {
    footnotes?: {
      refs?: Record<string, number>;
    };
  };
};

type Remarkable = {
  inline: {
    ruler: {
      __rules__: { name: string; fn: Function }[];
      at: (
        ruleName: string,
        fn: (state: RemarkableState, silent: boolean) => boolean,
      ) => void;
    };
  };
};
/**
 * A custom Remarkable plugin for modifying the image parsing logic. Remarkable
 * parses the markdown image as a custom token. We want our editor to only show
 * the markdown (text) version of the image.
 *
 * @see https://github.com/jonschlinkert/remarkable/blob/master/docs/plugins.md
 * @param md Remarkable instance
 */
const imageToMarkdownPlugin = (md: Remarkable) => {
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const linksRule = md.inline.ruler.__rules__.find(
    fn => fn.name === 'links',
  ).fn;

  if (linksRule) {
    md.inline.ruler.at('links', (state, silent) => {
      const res = linksRule(state, silent);

      state.tokens.forEach(token => {
        if (token.type === 'image') {
          token.content = `![${token.alt}](${token.src})`;
          token.type = 'text';
        }
      });

      return res;
    });
  }
};

/**
 * A custom Remarkable plugin for modifying the footnote parsing logic. Remarkable
 * parses the footnote as empty text. We want our editor to show
 * a link for the footnote instead.
 *
 * @see https://github.com/jonschlinkert/remarkable/blob/master/docs/plugins.md
 * @param md Remarkable instance
 */
const footnotePlugin = (md: Remarkable) => {
  const footnoteRule = md.inline.ruler.__rules__.find(
    fn => fn.name === 'footnote_ref',
  )?.fn;

  if (footnoteRule) {
    md.inline.ruler.at('footnote_ref', (state, silent) => {
      const res = footnoteRule(state, silent);

      const references: Record<number, string> = _invert(
        state.env?.footnotes?.refs ?? {},
      );
      state.tokens.forEach(token => {
        if (token.type === 'footnote_ref') {
          const referenceId = references[token.id ?? -1] ?? '';
          token.content = `[footnote_ref:${token.id}${referenceId}]`;
          token.type = 'text';
        }
      });

      return res;
    });
  }
};

const FOOTNOTE_REGEX = /\[footnote_ref:\d+:[^\]]*\]/g;

function footnoteStrategy(
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
) {
  const text = contentBlock.getText();
  let matchArr, start;
  while ((matchArr = FOOTNOTE_REGEX.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
}

export const convertMarkdownToEditorState = (
  markdown: string,
  preserveNewlines = true,
): EditorState => {
  const rawDraft = markdownToDraft(markdown, {
    preserveNewlines,
    remarkablePlugins: [imageToMarkdownPlugin, footnotePlugin],
  });
  const content = convertFromRaw(rawDraft);
  const editorState = EditorState.createWithContent(content);
  return editorState;
};

export const resetStateToMarkdown = (
  editorState: EditorState,
  markdown: string,
): EditorState => {
  const rawDraft = markdownToDraft(markdown, {
    preserveNewlines: true,
    remarkablePlugins: [imageToMarkdownPlugin, footnotePlugin],
  });
  const content = convertFromRaw(rawDraft);
  return EditorState.push(editorState, content, 'remove-range');
};

export const convertEditorStateToMarkdown = (
  editorState: EditorState,
): string => {
  const currentContent = editorState.getCurrentContent();
  const rawState = convertToRaw(currentContent);

  const markdown = draftToMarkdown(rawState, {
    preserveNewlines: true,
  });
  return markdown;
};

export const initializeEditorState = (
  markdown: string,
  preserveNewlines = true,
) => {
  return isEmpty(markdown)
    ? EditorState.createEmpty()
    : convertMarkdownToEditorState(markdown, preserveNewlines);
};

const lightThemePluginStyles = {
  link: 'lo-markdown-editor-light-link',
};

const darkThemePluginStyles = {
  link: 'lo-markdown-editor-dark-link',
};

class MarkdownEditor extends PureComponent<
  MarkdownEditorProps,
  MarkdownEditorState
> {
  editor: any = null;

  public state: MarkdownEditorState = {
    darkThemeLinkPlugin: createLinkPlugin({
      theme: darkThemePluginStyles,
    }),
    lightThemeLinkPlugin: createLinkPlugin({
      theme: lightThemePluginStyles,
    }),
    plugins: [],
    themedLinkPlugin: null,
    inlineToolbarPlugin: createInlineToolbarPlugin(),
  };

  componentDidMount() {
    const {
      darkThemeLinkPlugin,
      lightThemeLinkPlugin,
      plugins,
      inlineToolbarPlugin,
    } = this.state;
    const { whiteTheme } = this.props;

    const themedLinkPlugin = whiteTheme
      ? darkThemeLinkPlugin
      : lightThemeLinkPlugin;

    plugins.push(inlineToolbarPlugin);
    plugins.push(themedLinkPlugin); // link plugins must come before markdown plugin
    plugins.push(createMarkdownShortcutsPlugin());

    this.setState({
      themedLinkPlugin,
      plugins,
    });
  }

  componentDidUpdate(prevProps: MarkdownEditorProps) {
    // started editing
    if (!prevProps.isEditing && this.props.isEditing) {
      this.focusEditor();
    }
  }

  setEditor = (editor: any) => {
    this.editor = editor;
  };

  focusEditor = () => {
    if (this.editor) {
      this.editor.focus();
    }
  };

  moveSelectionToEnd = () => {
    if (this.editor) {
      let editorState: EditorState = this.editor.getEditorState();
      editorState = EditorState.moveSelectionToEnd(editorState);
      this.editor.update(
        EditorState.forceSelection(editorState, editorState.getSelection()),
      );
    }
  };

  toggleBlockType = (blockType: string) => {
    this.props.onChange(RichUtils.toggleBlockType(this.props.value, blockType));
  };

  toggleInlineStyle = (inlineStyle: string) => {
    this.props.onChange(
      RichUtils.toggleInlineStyle(this.props.value, inlineStyle),
    );
  };

  handleKeyCommand = (command: string, editorState: EditorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      this.props.onChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  handleMaintainFocus = (editorState: EditorState) => {
    const currentContentState = editorState.getCurrentContent();
    const willHaveFocus = currentContentState.getSelectionAfter().getHasFocus();

    if (!willHaveFocus) {
      return EditorState.moveFocusToEnd(editorState);
    }

    return editorState;
  };

  handleEditorClick: React.MouseEventHandler<HTMLDivElement> = e => {
    const target = e.target as HTMLElement;
    // If the user clicks just the editor box, move the cursor to the end
    if (target.classList.contains(this.props.classes.editorRoot)) {
      this.moveSelectionToEnd();
    }
  };

  handleOnChange = (editorState: EditorState) => {
    let newState = editorState;
    const textSelected = !editorState?.getSelection().isCollapsed();
    const lastChangeType = editorState?.getLastChangeType();
    if (
      editorState &&
      !textSelected &&
      [
        'move-block',
        'split-block',
        'backspace-character',
        'delete-character',
        'insert-characters',
        'insert-fragment',
        'remove-range',
        'redo',
        'undo',
      ].includes(lastChangeType)
    ) {
      newState = this.handleMaintainFocus(newState);
    }

    this.props.onChange(newState);

    return 'handled';
  };

  //github.com/facebook/draft-js/issues/1320#issuecomment-476509968
  handleBeforeInput = (chars: string, editorState: EditorState) => {
    const currentContentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    this.props.onChange(
      this.handleMaintainFocus(
        EditorState.push(
          editorState,
          Modifier.replaceText(currentContentState, selectionState, chars),
          'insert-characters',
        ),
      ),
    );

    return 'handled';
  };

  handlePaste = (chars: string, html: string, state: EditorState) => {
    const currentContentState = state.getCurrentContent();
    const selectionState = state.getSelection();

    // If there is html paste data, try to parse that.
    if (html) {
      const htmlFragment = DraftPasteProcessor.processHTML(
        html,
        this.editor.props.blockRenderMap,
      );
      if (htmlFragment?.contentBlocks) {
        const htmlMap = BlockMapBuilder.createFromArray(
          htmlFragment.contentBlocks,
        );
        this.props.onChange(
          this.handleMaintainFocus(
            EditorState.push(
              state,
              Modifier.replaceWithFragment(
                currentContentState,
                selectionState,
                htmlMap,
              ),
              'insert-fragment',
            ),
          ),
        );
        return 'handled';
      }
    }

    return this.handleBeforeInput(chars, state);
  };

  get decorators() {
    // todo: editing footnotes generated by AI is likely going to be problematic
    // maybe figure out how to make these readonly or something
    const Footnote = (props: any) => {
      // '[footnote_ref:1:dr0]' -> ['footnote_ref', 1, 'dr0]
      const [, id, reference] = props.decoratedText.slice(1, -1).split(':');
      const component = this.props.getComponentForFootnote?.(
        reference,
        id,
        props.decoratedText,
      );
      return component ?? null;
    };

    return [
      {
        strategy: footnoteStrategy,
        component: Footnote,
      },
    ];
  }

  render() {
    const {
      buttonControlClassName,
      classes,
      editorClassName,
      isEditing,
      disabled,
      editorBlockStyle,
      value,
      whiteTheme,
      placeholderText,
    } = this.props;
    const { plugins, themedLinkPlugin, inlineToolbarPlugin } = this.state;

    if (plugins && plugins.length === 0) {
      return null;
    }

    const hasContent = value.getCurrentContent().hasText();

    const editor = (
      <>
        <Editor
          webDriverTestID="markdown-editor"
          className={clsx(classes.editorRoot, editorClassName)}
          readOnly={!isEditing || disabled}
          ref={this.setEditor}
          editorState={value}
          handleKeyCommand={this.handleKeyCommand}
          plugins={this.state.plugins}
          decorators={this.decorators}
          blockStyleFn={editorBlockStyle}
          onChange={this.handleOnChange}
          handleBeforeInput={this.handleBeforeInput}
          onClick={this.focusEditor}
          placeholder={placeholderText}
          handlePastedText={this.handlePaste}
          handlePastedFiles={this.props.handlePastedFile}
        />
        <inlineToolbarPlugin.InlineToolbar>
          {(externalProps: any) => (
            <React.Fragment>
              <BoldButton {...externalProps} />
              <ItalicButton {...externalProps} />
              <UnderlineButton {...externalProps} />
              <themedLinkPlugin.LinkButton {...externalProps} />
            </React.Fragment>
          )}
        </inlineToolbarPlugin.InlineToolbar>
      </>
    );

    const editorWithAnimation = hasContent ? (
      editor
    ) : (
      <Collapse in={isEditing}>{editor}</Collapse>
    );

    return (
      <Box
        className={clsx(
          classes.editorRoot,
          !disabled && classes.enabledEditorRoot,
          editorClassName,
        )}
        onClick={this.handleEditorClick}
      >
        <ErrorBoundary>
          <Collapse in={isEditing}>
            <Controls
              buttonClassName={buttonControlClassName}
              onToggleBlockType={this.toggleBlockType}
              onToggleInlineStyle={this.toggleInlineStyle}
              editorState={value}
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              disabled={disabled}
              whiteTheme={whiteTheme}
            />
          </Collapse>
          {editorWithAnimation}
        </ErrorBoundary>
      </Box>
    );
  }
}

export default withStyles(styles)(MarkdownEditor);
